import { useEffect, useRef, useCallback } from 'react';
import { useKeybindingContext } from './KeybindingContext.jsx';
import actionExecutor from './ActionExecutor.js';

/**
 * Main hook for registering and managing keybindings in components
 * 
 * @param {Object|Function} bindings - Keybinding definitions or factory function
 * @param {Array} deps - Dependency array for re-registration
 * @param {Object} options - Additional options
 * @returns {Object} - Keybinding utilities
 */
export const useKeybinding = (bindings = {}, deps = [], options = {}) => {
  const context = useKeybindingContext();
  const registeredIds = useRef([]); // Array of { sequence, actionId }
  const bindingsRef = useRef(bindings);

  // Update bindings ref when they change
  bindingsRef.current = bindings;

  // Register keybindings
  useEffect(() => {
    // Clear previous registrations
    for (const { sequence, actionId } of registeredIds.current) {
      context.unregister(sequence);
      actionExecutor.unregister(actionId);
    }
    registeredIds.current = [];

    // Resolve bindings (could be function or object)
    const resolvedBindings = typeof bindings === 'function' ? bindings() : bindings;
    
    if (!resolvedBindings || typeof resolvedBindings !== 'object') {
      return;
    }

    // Register new bindings
    for (const [sequence, definition] of Object.entries(resolvedBindings)) {
      try {
        // Handle different binding definition formats
        let handler, description, when, scope;
        
        if (typeof definition === 'function') {
          // Simple function handler
          handler = definition;
          description = '';
        } else if (typeof definition === 'object') {
          // Detailed binding object
          handler = definition.handler || definition.action;
          description = definition.description || definition.desc || '';
          when = definition.when;
          scope = definition.scope;
        } else {
          console.warn(`[useKeybinding] Invalid binding definition for "${sequence}":`, definition);
          continue;
        }

        if (typeof handler !== 'function') {
          console.warn(`[useKeybinding] No handler function for "${sequence}"`);
          continue;
        }

        // Create unique action ID
        const actionId = `component-${sequence}-${Math.random().toString(36).substr(2, 9)}`;
        
        // Register action with executor
        actionExecutor.register(actionId, handler, {
          description,
          category: options.category || 'component',
          source: 'useKeybinding',
        });
        
        // Register keybinding
        context.register(sequence, actionId, {
          description,
          scope: scope || options.scope || 'component',
          when: when || (() => true),
          preventDefault: options.preventDefault !== false,
        });
        
        registeredIds.current.push({ sequence, actionId });
        
        if (import.meta.env.DEV && false) { // Temporarily disabled for user testing
          console.log(`[useKeybinding] Registered: ${sequence} → ${actionId}`);
        }
        
      } catch (error) {
        console.error(`[useKeybinding] Failed to register binding "${sequence}":`, error);
      }
    }

    // Cleanup function
    return () => {
      for (const { sequence, actionId } of registeredIds.current) {
        context.unregister(sequence);
        actionExecutor.unregister(actionId);
      }
      registeredIds.current = [];
    };
  }, [bindings, ...deps, options.scope, options.category]);

  return {
    // State
    isBuilding: context.isBuilding,
    currentSequence: context.currentSequence,
    enabled: context.enabled,
    
    // Controls
    resetSequence: context.resetSequence,
    setEnabled: context.setEnabled,
    
    // Manual execution
    execute: context.executeAction,
    
    // Registry access
    getBindings: () => context.getAllBindings(),
    getStats: () => context.getStats(),
  };
};

/**
 * Hook for global keybindings that persist across route changes
 * 
 * @param {Object} bindings - Global keybinding definitions
 * @param {Array} deps - Dependency array
 * @returns {Object} - Keybinding utilities
 */
export const useGlobalKeybinding = (bindings, deps = []) => {
  return useKeybinding(bindings, deps, { 
    scope: 'global',
    category: 'global' 
  });
};

/**
 * Hook for registering a single keybinding
 * 
 * @param {string} sequence - Key sequence
 * @param {Function} handler - Handler function
 * @param {Array} deps - Dependencies
 * @param {Object} options - Options
 * @returns {Object} - Keybinding utilities
 */
export const useKeyBinding = (sequence, handler, deps = [], options = {}) => {
  const bindings = {
    [sequence]: {
      handler,
      ...options
    }
  };
  
  return useKeybinding(bindings, deps, options);
};

/**
 * Hook for conditional keybindings
 * 
 * @param {Object} bindings - Keybinding definitions with when conditions
 * @param {Array} deps - Dependencies
 * @param {Object} options - Options
 * @returns {Object} - Keybinding utilities
 */
export const useConditionalKeybinding = (bindings, deps = [], options = {}) => {
  return useKeybinding(bindings, deps, options);
};

/**
 * Hook for modal/temporary keybindings
 * 
 * @param {Object} bindings - Temporary keybinding definitions
 * @param {boolean} active - Whether bindings should be active
 * @param {Array} deps - Dependencies
 * @returns {Object} - Keybinding utilities
 */
export const useModalKeybinding = (bindings, active = true, deps = []) => {
  const activeBindings = active ? bindings : {};
  
  return useKeybinding(activeBindings, [active, ...deps], {
    scope: 'modal',
    category: 'temporary'
  });
};

/**
 * Hook for accessing keybinding statistics and debugging info
 * 
 * @returns {Object} - Statistics and debugging utilities
 */
export const useKeybindingStats = () => {
  const context = useKeybindingContext();
  
  return {
    registryStats: context.getStats(),
    executorStats: context.executor.getStats(),
    history: context.history,
    allBindings: context.getAllBindings(),
    export: () => ({
      registry: context.registry.export(),
      executor: context.executor.export(),
      state: {
        currentSequence: context.currentSequence,
        isBuilding: context.isBuilding,
        enabled: context.enabled,
        history: context.history,
      }
    }),
  };
};

/**
 * Hook for keybinding help and discovery
 * 
 * @param {string} filter - Optional filter for bindings
 * @returns {Object} - Help utilities
 */
export const useKeybindingHelp = (filter = '') => {
  const context = useKeybindingContext();
  
  const getAvailableBindings = useCallback(() => {
    const bindings = context.getAllBindings({ active: true });
    
    if (!filter) return bindings;
    
    return bindings.filter(binding => 
      binding.sequence.toLowerCase().includes(filter.toLowerCase()) ||
      binding.description.toLowerCase().includes(filter.toLowerCase())
    );
  }, [filter, context]);
  
  const getCompletions = useCallback((partialSequence) => {
    return context.registry.getCompletions(partialSequence);
  }, [context]);
  
  return {
    getAvailableBindings,
    getCompletions,
    isBuilding: context.isBuilding,
    currentSequence: context.currentSequence,
  };
};

/**
 * Hook for programmatically controlling keybinding system
 * 
 * @returns {Object} - Control utilities
 */
export const useKeybindingControl = () => {
  const context = useKeybindingContext();
  
  return {
    enabled: context.enabled,
    setEnabled: context.setEnabled,
    resetSequence: context.resetSequence,
    execute: context.executeAction,
    
    // Advanced controls
    clearHistory: () => context.executor.clearHistory(),
    addMiddleware: (middleware) => context.executor.use(middleware),
    
    // Emergency functions
    clearAllBindings: () => context.registry.clear(),
    disableTemporarily: (ms) => {
      context.setEnabled(false);
      setTimeout(() => context.setEnabled(true), ms);
    },
  };
};